import { useEffect } from "react";
import { getHost } from "./getHost";

// Watches our document's content height and reports it back to the host
// via the zoid `onHeightChange` bridge. The host sets the iframe element's
// height to match, so our app renders flush with no inner scrollbar and
// the outer page scrolls if needed.
//
// Standalone dev (no host): no-op. The ResizeObserver still runs but the
// emit is skipped, so nothing breaks when running outside zoid.

interface OnHeightChange {
  (height: number | null): void;
}

interface HostWithHeightChange {
  onHeightChange?: OnHeightChange;
}

export function useHostHeightReporter(): void {
  useEffect(() => {
    const target = document.documentElement;
    if (!target || typeof ResizeObserver === "undefined") return;

    let lastReported = -1;
    const report = () => {
      const host = getHost() as HostWithHeightChange | null;
      if (!host?.onHeightChange) return;
      // scrollHeight is the natural content height including overflow.
      // documentElement.scrollHeight is the document height.
      const height = Math.ceil(target.scrollHeight);
      if (height === lastReported) return;
      lastReported = height;
      host.onHeightChange(height);
    };

    const observer = new ResizeObserver(() => report());
    observer.observe(target);
    // Fire once immediately so the host doesn't wait for the first resize.
    report();

    return () => observer.disconnect();
  }, []);
}
